<template>
  <div class="agent-page">
    <section class="stats-grid">
      <article class="stat-card cyan-card">
        <div class="stat-head">
          <span>今日调用次数</span>
          <el-icon>
            <DataAnalysis />
          </el-icon>
        </div>
        <strong>{{ formatNumber(summary.todayCalls) }}</strong>
        <em :class="{ up: summary.callsRate >= 0, down: summary.callsRate < 0 }">{{ formatRate(summary.callsRate) }}</em>
        <i class="spark-line"></i>
      </article>
      <article class="stat-card violet-card">
        <div class="stat-head">
          <span>今日扣分</span>
          <el-icon>
            <Coin />
          </el-icon>
        </div>
        <strong>{{ formatNumber(summary.todayPoints) }}</strong>
        <em :class="{ up: summary.pointsRate >= 0, down: summary.pointsRate < 0 }">{{ formatRate(summary.pointsRate) }}</em>
        <i class="spark-line"></i>
      </article>
      <article class="stat-card amber-card">
        <div class="stat-head">
          <span>今日充值金额</span>
          <el-icon>
            <Wallet />
          </el-icon>
        </div>
        <strong>￥{{ formatMoney(summary.todayRechargeAmount) }}</strong>
        <em :class="{ up: summary.rechargeRate >= 0, down: summary.rechargeRate < 0 }">{{ formatRate(summary.rechargeRate) }}</em>
        <i class="spark-line"></i>
      </article>
      <article class="stat-card blue-card">
        <div class="stat-head">
          <span>活跃用户</span>
          <el-icon>
            <UserFilled />
          </el-icon>
        </div>
        <strong>{{ formatNumber(summary.todayActiveUsers) }}</strong>
        <em :class="{ up: summary.activeRate >= 0, down: summary.activeRate < 0 }">{{ formatRate(summary.activeRate) }}</em>
        <i class="spark-line"></i>
      </article>
      <article class="stat-card green-card">
        <div class="stat-head">
          <span>累计收益</span>
          <el-icon>
            <Money />
          </el-icon>
        </div>
        <strong>￥{{ formatMoney(summary.totalRevenue) }}</strong>
        <em>可提现 ￥{{ formatMoney(summary.withdrawableAmount) }}</em>
        <i class="spark-line"></i>
      </article>
      <article class="stat-card slate-card">
        <div class="stat-head">
          <span>已提现</span>
          <el-icon>
            <CreditCard />
          </el-icon>
        </div>
        <strong>￥{{ formatMoney(summary.withdrawnAmount) }}</strong>
        <em>代理 {{ formatNumber(summary.agentCount) }} 个</em>
        <i class="spark-line"></i>
      </article>
    </section>

    <section class="content-panel">
      <div class="table-title">
        <div>
          <h3>代理列表</h3>
          <p>按代理域名聚合今日数据和收益信息。</p>
        </div>
        <div class="table-tools">
          <el-input v-model="query.keyword" clearable placeholder="搜索手机号 / 域名 / 名称"
            @keyup.enter="handleAgentSearch" @clear="handleAgentSearch">
            <template #prefix>
              <el-icon>
                <Search />
              </el-icon>
            </template>
          </el-input>
          <el-select v-model="query.status" clearable placeholder="状态">
            <el-option label="正常" :value="1" />
            <el-option label="封禁" :value="0" />
          </el-select>
          <el-select v-model="query.sortBy" placeholder="排序" @change="handleSortChange">
            <el-option label="今日调用从高到低" value="todayCalls" />
            <el-option label="今日扣分从高到低" value="todayPoints" />
            <el-option label="失败积分从高到低" value="todayFailedPoints" />
            <el-option label="今日充值从高到低" value="todayRechargeAmount" />
            <el-option label="活跃用户从高到低" value="todayActiveUsers" />
            <el-option label="累计收益从高到低" value="totalRevenue" />
          </el-select>
          <el-button type="primary" :loading="loading" @click="handleAgentSearch">搜索</el-button>
          <span>共 {{ total }} 个代理</span>
        </div>
      </div>

      <el-table :data="agentList" v-loading="loading" class="agent-table" height="calc(100vh - 420px)">
        <el-table-column label="代理信息" min-width="230" fixed>
          <template #default="{ row }">
            <div class="agent-cell">
              <el-avatar :src="getLogoUrl(row.logo)" :size="42">{{ getAvatarText(row) }}</el-avatar>
              <div>
                <strong>{{ row.site_name || row.name || '未配置名称' }}</strong>
                <span>{{ row.phone }} · {{ row.domain || '-' }}</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="代理类型" width="95" align="center">
          <template #default="{ row }">
            <el-tag :type="row.brandtype === 'two' ? 'warning' : 'success'" effect="dark" size="small">
              {{ row.brandtype === 'two' ? '二级代理' : '一级代理' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="上游手机号" width="135" align="center">
          <template #default="{ row }">
            {{ row.brandtype === 'two' ? (row.parent_phone || '-') : '-' }}
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template #default="{ row }">
            <el-tag :type="Number(row.status) === 1 ? 'success' : 'danger'" effect="dark" size="small">
              {{ Number(row.status) === 1 ? '正常' : '封禁' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="reward_ratio" label="分成比例" width="100">
          <template #default="{ row }">{{ formatNumber(row.reward_ratio) }}%</template>
        </el-table-column>
        <el-table-column label="今日调用" width="105">
          <template #default="{ row }">{{ formatNumber(row.todayCalls) }}</template>
        </el-table-column>
        <el-table-column label="今日扣分" width="110">
          <template #default="{ row }">{{ formatNumber(row.todayPoints) }}</template>
        </el-table-column>
        <el-table-column label="失败积分" width="110">
          <template #default="{ row }">
            <span class="failed-points">{{ formatNumber(row.todayFailedPoints) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="今日充值" width="120">
          <template #default="{ row }">￥{{ formatMoney(row.todayRechargeAmount) }}</template>
        </el-table-column>
        <el-table-column label="活跃用户" width="100">
          <template #default="{ row }">{{ formatNumber(row.todayActiveUsers) }}</template>
        </el-table-column>
        <el-table-column label="可提现" width="120">
          <template #default="{ row }">
            <span class="money-text">￥{{ formatMoney(row.withdrawableAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="已提现" width="120">
          <template #default="{ row }">￥{{ formatMoney(row.withdrawnAmount) }}</template>
        </el-table-column>
        <el-table-column label="累计收益" width="120">
          <template #default="{ row }">￥{{ formatMoney(row.totalRevenue) }}</template>
        </el-table-column>
        <el-table-column label="注册时间" width="165">
          <template #default="{ row }">{{ formatTime(row.regTime || row.create_time) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template #default="{ row }">
            <el-button class="ghost-btn" size="small" @click="openPointsLog(row)">消费记录</el-button>
          </template>
        </el-table-column>

        <template #empty>
          <el-empty description="暂无代理数据" />
        </template>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination v-model:current-page="query.page" v-model:page-size="query.pageSize" :total="total"
          :page-sizes="[10, 20, 50, 100]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="handlePageSizeChange" @current-change="fetchAgents" />
      </div>
    </section>

    <el-dialog v-model="logDialogVisible" :title="`${currentAgent?.site_name || currentAgent?.phone || ''} 消费记录`"
      width="980px" class="agent-dialog">
      <div class="dialog-toolbar">
        <el-input v-model="logQuery.searchQuery" clearable placeholder="搜索用户手机号 / 用户名" @keyup.enter="fetchPointsLog" />
        <el-select v-model="logQuery.status" clearable placeholder="状态">
          <el-option label="成功" value="成功" />
          <el-option label="失败" value="失败" />
          <el-option label="进行中" value="进行中" />
        </el-select>
        <el-button type="primary" :loading="logLoading" @click="fetchPointsLog">查询</el-button>
      </div>

      <el-table :data="pointsLogList" v-loading="logLoading" class="agent-table" height="430px">
        <el-table-column prop="CreationTime" label="时间" width="170">
          <template #default="{ row }">{{ formatTime(row.CreationTime) }}</template>
        </el-table-column>
        <el-table-column prop="phone" label="用户手机号" width="130" />
        <el-table-column prop="username" label="用户名" width="130" show-overflow-tooltip />
        <el-table-column prop="model_name" label="模型" min-width="140" show-overflow-tooltip />
        <el-table-column prop="model_type" label="类型" width="90" />
        <el-table-column prop="points" label="扣分" width="100">
          <template #default="{ row }">{{ formatNumber(row.points) }}</template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" />
      </el-table>

      <div class="pagination-wrap">
        <el-pagination v-model:current-page="logQuery.page" v-model:page-size="logQuery.pageSize" :total="logTotal"
          :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
          @size-change="handleLogPageSizeChange" @current-change="fetchPointsLog" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { Coin, CreditCard, DataAnalysis, Money, Search, UserFilled, Wallet } from '@element-plus/icons-vue'
import axios from 'axios'

const loading = ref(false)
const logLoading = ref(false)
const logDialogVisible = ref(false)
const agentList = ref([])
const pointsLogList = ref([])
const currentAgent = ref(null)
const total = ref(0)
const logTotal = ref(0)

const summary = reactive({
  todayCalls: 0,
  todayPoints: 0,
  todayFailedPoints: 0,
  todayRechargeAmount: 0,
  todayActiveUsers: 0,
  totalRevenue: 0,
  withdrawnAmount: 0,
  withdrawableAmount: 0,
  agentCount: 0,
  callsRate: 0,
  pointsRate: 0,
  rechargeRate: 0,
  activeRate: 0
})

const query = reactive({
  keyword: '',
  status: '',
  sortBy: 'todayCalls',
  sortOrder: 'desc',
  page: 1,
  pageSize: 20
})

const logQuery = reactive({
  searchQuery: '',
  status: '',
  page: 1,
  pageSize: 20
})

const fetchAgents = async () => {
  loading.value = true
  try {
    const res = await axios.get('/admin/getAgentOverviewList', { params: query })
    if (res.data?.msg === 'success') {
      const data = res.data.data || {}
      agentList.value = data.list || []
      total.value = Number(data.total || agentList.value.length || 0)
      Object.assign(summary, data.summary || {})
    } else {
      ElMessage.error(res.data?.message || res.data?.error || '获取代理列表失败')
    }
  } catch (error) {
    ElMessage.error('代理总览接口暂未接入或请求失败')
  } finally {
    loading.value = false
  }
}

const handleAgentSearch = () => {
  query.page = 1
  fetchAgents()
}

const openPointsLog = (row) => {
  currentAgent.value = row
  logQuery.searchQuery = ''
  logQuery.status = ''
  logQuery.page = 1
  logDialogVisible.value = true
  fetchPointsLog()
}

const fetchPointsLog = async () => {
  if (!currentAgent.value) return
  logLoading.value = true
  try {
    const res = await axios.get('/admin/getAgentUserPointsLog', {
      params: {
        agentPhone: currentAgent.value.phone,
        currentPage: logQuery.page,
        pageSize: logQuery.pageSize,
        searchQuery: logQuery.searchQuery,
        status: logQuery.status
      }
    })
    if (res.data?.msg === 'success') {
      const data = res.data.data || {}
      pointsLogList.value = data.list || []
      logTotal.value = Number(data.total || 0)
    } else {
      ElMessage.error(res.data?.message || res.data?.error || '获取消费记录失败')
    }
  } catch (error) {
    ElMessage.error('消费记录接口暂未接入或请求失败')
  } finally {
    logLoading.value = false
  }
}

const handlePageSizeChange = () => {
  query.page = 1
  fetchAgents()
}

const handleSortChange = () => {
  query.page = 1
  query.sortOrder = 'desc'
  fetchAgents()
}

const handleLogPageSizeChange = () => {
  logQuery.page = 1
  fetchPointsLog()
}

const getAvatarText = (row) => String(row.site_name || row.name || row.phone || '代').slice(0, 1)
const getLogoUrl = (logo) => {
  if (!logo) return ''
  const value = String(logo).trim()
  if (!value) return ''
  if (/^(https?:)?\/\//i.test(value) || value.startsWith('data:') || value.startsWith('blob:')) {
    return value
  }

  const baseURL = axios.defaults.baseURL || window.location.origin
  if (value.startsWith('/api/getfile') || value.startsWith('/getfile')) {
    return `${baseURL}${value.startsWith('/api') ? value : `/api${value}`}`
  }

  return `${baseURL}/api/getfile?filename=${encodeURIComponent(value)}`
}
const formatNumber = (value) => Number(value || 0).toLocaleString('zh-CN', { maximumFractionDigits: 2 })
const formatMoney = (value) => Number(value || 0).toLocaleString('zh-CN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})
const formatRate = (value) => {
  const num = Number(value || 0)
  return `${num >= 0 ? '较昨日 +' : '较昨日 '}${num.toFixed(2)}%`
}
const formatTime = (time) => {
  if (!time) return '-'
  return String(time).replace('T', ' ').split('.')[0]
}

onMounted(fetchAgents)
</script>

<style scoped>
.agent-page {
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  padding: 20px;
  background: #060a12;
  color: #e8edf7;
  display: flex;
  flex-direction: column;
}

:global(.el-main:has(.agent-page)) {
  height: calc(100vh - 60px);
  padding: 0;
  overflow: hidden;
  background: #060a12;
}

.content-panel {
  border: 1px solid rgba(94, 116, 150, 0.22);
  border-radius: 8px;
  background: #0d1422;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.26);
}

.stats-grid {
  flex-shrink: 0;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.stat-card {
  position: relative;
  min-height: 128px;
  padding: 18px 18px 16px;
  box-sizing: border-box;
  overflow: hidden;
  border: 1px solid rgba(94, 116, 150, 0.22);
  border-radius: 8px;
  background: #101827;
  isolation: isolate;
}

.stat-card::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: -1;
}

.stat-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.stat-head .el-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  color: #e8f3ff;
  background: rgba(255, 255, 255, 0.08);
}

.cyan-card::before {
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.2), transparent 62%);
}

.violet-card::before {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.2), transparent 62%);
}

.amber-card {
  border-color: rgba(255, 116, 23, 0.34);
}

.amber-card::before {
  background: linear-gradient(135deg, rgba(255, 116, 23, 0.22), transparent 62%);
}

.blue-card::before {
  background: linear-gradient(135deg, rgba(79, 70, 229, 0.2), transparent 62%);
}

.green-card::before {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.18), transparent 62%);
}

.slate-card::before {
  background: linear-gradient(135deg, rgba(148, 163, 184, 0.14), transparent 62%);
}

.stat-card span,
.stat-card em {
  display: block;
  color: #9fb0c7;
  font-style: normal;
  font-size: 13px;
}

.stat-card strong {
  display: block;
  margin: 16px 0 12px;
  color: #fff;
  font-size: 27px;
  line-height: 1.1;
}

.stat-card em.up {
  color: #22c55e;
}

.stat-card em.down {
  color: #cbd5e1;
}

.spark-line {
  position: absolute;
  right: 18px;
  bottom: 18px;
  width: 82px;
  height: 24px;
  opacity: 0.85;
  border-bottom: 2px solid currentColor;
  border-radius: 50%;
}

.spark-line::before,
.spark-line::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: currentColor;
}

.spark-line::before {
  width: 4px;
  height: 4px;
  left: 18px;
  bottom: 8px;
  box-shadow: 28px -7px 0 currentColor, 56px -2px 0 currentColor;
}

.spark-line::after {
  width: 44px;
  height: 18px;
  right: 0;
  bottom: 3px;
  border: 2px solid currentColor;
  border-color: transparent transparent currentColor transparent;
  background: transparent;
}

.cyan-card .spark-line {
  color: #0ea5e9;
}

.violet-card .spark-line {
  color: #8b5cf6;
}

.amber-card .spark-line {
  color: #ff7417;
}

.blue-card .spark-line {
  color: #6366f1;
}

.green-card .spark-line {
  color: #22c55e;
}

.slate-card .spark-line {
  color: #94a3b8;
}

.content-panel {
  flex: 1;
  min-height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.table-title {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  padding: 2px 2px 14px;
}

.table-title h3 {
  margin: 0 0 5px;
  color: #f5f7fb;
  font-size: 18px;
}

.table-title p,
.table-title span {
  margin: 0;
  color: #8f9bad;
  font-size: 13px;
}

.table-tools {
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(180px, 260px) 100px 170px 76px auto;
  gap: 10px;
  align-items: center;
}

.table-tools span {
  white-space: nowrap;
  text-align: right;
}

.agent-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.agent-cell strong,
.agent-cell span {
  display: block;
}

.agent-cell strong {
  color: #f8fafc;
  font-size: 14px;
}

.agent-cell span {
  margin-top: 4px;
  color: #8f9bad;
  font-size: 12px;
}

.money-text {
  color: #22c55e;
  font-weight: 700;
}

.failed-points {
  color: #f56c6c;
  font-weight: 700;
}

.pagination-wrap {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
}

.dialog-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 120px 82px;
  gap: 10px;
  margin-bottom: 14px;
}

.ghost-btn {
  border-color: #314059;
  background: #121a28;
  color: #d6deea;
  border-radius: 6px;
}

.ghost-btn:hover {
  border-color: #ff7417;
  color: #ff9b4a;
  background: #161f2f;
}

:deep(.el-input__wrapper),
:deep(.el-select__wrapper) {
  background: #121a28;
  border: 1px solid #2c3a50;
  border-radius: 6px;
  box-shadow: none;
  color: #e8edf7;
}

:deep(.el-input__wrapper:hover),
:deep(.el-select__wrapper:hover),
:deep(.el-input__wrapper.is-focus),
:deep(.el-select__wrapper.is-focused) {
  border-color: #ff7417;
}

:deep(.el-input__inner),
:deep(.el-select__placeholder),
:deep(.el-select__selected-item) {
  color: #e8edf7;
}

:deep(.el-table),
:deep(.el-table tr),
:deep(.el-table th.el-table__cell),
:deep(.el-table td.el-table__cell) {
  background-color: #0f1725;
  color: #e5eaf3;
  border-bottom-color: rgba(94, 116, 150, 0.22);
}

:deep(.el-table th.el-table__cell) {
  background-color: #111b2b;
  color: #aebbd0;
  font-weight: 700;
}

:deep(.el-table .el-table__cell.is-hidden > *) {
  visibility: visible;
}

:deep(.el-table__fixed),
:deep(.el-table__fixed-right),
:deep(.el-table__fixed-header-wrapper),
:deep(.el-table__fixed-body-wrapper),
:deep(.el-table__fixed .el-table__cell),
:deep(.el-table__fixed-right .el-table__cell) {
  background-color: #0f1725 !important;
}

:deep(.el-table__fixed-header-wrapper th.el-table__cell),
:deep(.el-table__fixed-right .el-table__fixed-header-wrapper th.el-table__cell),
:deep(.el-table th.el-table-fixed-column--left),
:deep(.el-table th.el-table-fixed-column--right) {
  background-color: #111b2b !important;
  color: #aebbd0;
}

:deep(.el-table td.el-table-fixed-column--left),
:deep(.el-table td.el-table-fixed-column--right) {
  background-color: #0f1725 !important;
}

:deep(.el-table tbody tr:hover > td.el-table__cell) {
  background-color: #172235 !important;
}

:deep(.el-table tbody tr:hover > td.el-table-fixed-column--left),
:deep(.el-table tbody tr:hover > td.el-table-fixed-column--right) {
  background-color: #172235 !important;
}

:deep(.el-table::before),
:deep(.el-table__inner-wrapper::before) {
  background-color: #263142;
}

/* 推广详情表格加载遮罩：适配暗色背景，避免默认白色遮罩刺眼 */
:deep(.agent-table .el-loading-mask) {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

:deep(.agent-table .el-loading-spinner .path) {
  stroke: #409eff;
}

:deep(.el-dialog) {
  background: #0d131f;
  border: 1px solid #263142;
}

:deep(.el-dialog__title) {
  color: #f8fafc;
}

:deep(.el-pagination) {
  --el-pagination-bg-color: #121a28;
  --el-pagination-button-disabled-bg-color: #0d131f;
  --el-pagination-hover-color: #ff9b4a;
  --el-pagination-text-color: #cbd5e1;
  --el-pagination-button-color: #cbd5e1;
}

@media (max-width: 1400px) {
  .stats-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 1200px) {
  .stats-grid {
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .table-title {
    align-items: stretch;
    flex-direction: column;
  }

  .table-tools {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
